import React, { useState, useCallback } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSGrid from "views/system-design/DSGrid";
import DSChip from "views/system-design/DSChip";
import PageHeader from "components/PageHeader";
import { useTesterStatus } from "../hooks/useTesterStatus";
import { useVersionTesterSocket } from "../hooks/useVersionTesterSocket";
import DeviceCard from "./DeviceCard";
import TestForm from "./TestForm";
import TestProgress from "./TestProgress";
import ErrorSummaryPanel from "./ErrorSummaryPanel";
import AnalysisHistory from "./AnalysisHistory";
import type {
  WsMessage,
  WsDeviceStatusPayload,
  WsErrorDetectedPayload,
  WsTestCompletedPayload,
  WsStoryStartedPayload,
  WsStoryStepPayload,
  WsStoryCompletedPayload,
  TestResult,
  TestStatus,
  StoryResult,
} from "../types";
import type { DeviceStatus, StoryProgressItem } from "./TestProgress";

/**
 * Version Tester main page
 *
 * Displays:
 * - Connected devices with status and version info
 * - Test configuration form (version input, factory reset toggles)
 * - WebSocket connection indicator (Live/Offline)
 * - Real-time test progress during execution (per-device status updates)
 * - Error summary when test completes
 * - Error state when tester server is unreachable
 */
const VersionTester: React.FC = () => {
  const { data: status, isLoading, error } = useTesterStatus();

  // State for real-time test progress
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [deviceStatuses, setDeviceStatuses] = useState<
    Record<string, DeviceStatus>
  >({});
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [storyProgress, setStoryProgress] = useState<
    Record<string, Record<string, StoryProgressItem>>
  >({});

  // WebSocket message handler for real-time updates
  const handleWsMessage = useCallback((msg: WsMessage) => {
    switch (msg.type) {
      case "test:started":
        setIsTestRunning(true);
        setDeviceStatuses({});
        setTestResult(null);
        setStoryProgress({});
        break;

      case "test:device-status": {
        const payload = msg.payload as WsDeviceStatusPayload;
        setDeviceStatuses((prev) => ({
          ...prev,
          [payload.deviceId]: {
            deviceId: payload.deviceId,
            status: payload.status,
            step: payload.step,
            errorsDetected: prev[payload.deviceId]?.errorsDetected ?? 0,
          },
        }));
        break;
      }

      case "test:error-detected": {
        const payload = msg.payload as WsErrorDetectedPayload;
        setDeviceStatuses((prev) => ({
          ...prev,
          [payload.deviceId]: {
            ...prev[payload.deviceId],
            errorsDetected: (prev[payload.deviceId]?.errorsDetected ?? 0) + 1,
          },
        }));
        break;
      }

      case "story:started": {
        const payload = msg.payload as WsStoryStartedPayload;
        setStoryProgress((prev) => {
          const deviceStories = prev[payload.deviceId] || {};

          // Initialize all stories for this device as 'pending' if not already present
          const updatedDeviceStories = { ...deviceStories };
          for (let i = 0; i < payload.totalStories; i++) {
            const storyKey = `${i}`;
            if (!updatedDeviceStories[storyKey]) {
              updatedDeviceStories[storyKey] = {
                storyId: "",
                storyName: "",
                storyIndex: i,
                totalStories: payload.totalStories,
                status: "pending",
              };
            }
          }

          // Set the current story to 'running'
          updatedDeviceStories[payload.storyId] = {
            storyId: payload.storyId,
            storyName: payload.storyName,
            storyIndex: payload.storyIndex,
            totalStories: payload.totalStories,
            status: "running",
          };

          return {
            ...prev,
            [payload.deviceId]: updatedDeviceStories,
          };
        });
        break;
      }

      case "story:step": {
        const payload = msg.payload as WsStoryStepPayload;
        setStoryProgress((prev) => {
          const deviceStories = prev[payload.deviceId] || {};
          const currentStory = deviceStories[payload.storyId];

          return {
            ...prev,
            [payload.deviceId]: {
              ...deviceStories,
              [payload.storyId]: {
                ...currentStory,
                storyId: payload.storyId,
                storyName: payload.storyName,
                storyIndex: payload.storyIndex,
                totalStories: payload.totalStories,
                status: "running",
                currentStep: payload.stepIndex + 1,
                latestReasoning: payload.action.reasoning,
              },
            },
          };
        });
        break;
      }

      case "story:completed": {
        const payload = msg.payload as WsStoryCompletedPayload;
        setStoryProgress((prev) => {
          const deviceStories = prev[payload.deviceId] || {};
          const currentStory = deviceStories[payload.storyId];

          return {
            ...prev,
            [payload.deviceId]: {
              ...deviceStories,
              [payload.storyId]: {
                ...currentStory,
                storyId: payload.storyId,
                storyName: payload.storyName,
                storyIndex: payload.storyIndex,
                totalStories: payload.totalStories,
                status: payload.result.verdict,
                result: payload.result,
              },
            },
          };
        });
        break;
      }

      case "test:device-complete": {
        // Device status will be set via test:device-status message
        break;
      }

      case "test:completed": {
        const payload = msg.payload as WsTestCompletedPayload;
        setIsTestRunning(false);
        setTestResult(payload.result);
        break;
      }

      case "connection:status":
        // Ignore - already handled by useTesterStatus polling
        break;

      default:
        console.warn("[VersionTester] Unknown WS message type:", msg.type);
    }
  }, []);

  // Connect to WebSocket for real-time updates
  const { connected: wsConnected } = useVersionTesterSocket({
    onMessage: handleWsMessage,
    enabled: true,
  });

  const hasDevices = status?.devices && status.devices.length > 0;

  const pageHeader = (
    <PageHeader
      overline="Devices"
      title="Version Tester"
      subtitle="Run automated test stories on connected lab clocks and review the results"
      actions={
        wsConnected ? (
          <DSChip label="Live" color="success" size="small" />
        ) : (
          <DSChip label="Offline" color="default" size="small" />
        )
      }
    />
  );

  const handleTestStarted = () => {
    // Reset state for new test (immediate UI feedback before WS message arrives)
    setDeviceStatuses({});
    setTestResult(null);
    setIsTestRunning(true);
  };

  // Loading state
  if (isLoading && !status) {
    return (
      <DSBox>
        {pageHeader}
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <DSCircularProgress />
        </DSBox>
      </DSBox>
    );
  }

  // Error state
  if (error && !status) {
    return (
      <DSBox>
        {pageHeader}
        <DSTypography variant="body1" color="error">
          Cannot connect to tester server. Is it running?
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox>
      {pageHeader}

      {/* Connected devices section */}
      <DSTypography variant="h6" sx={{ mb: 2 }}>
        Connected Devices
      </DSTypography>

      {hasDevices ? (
        <DSGrid container spacing={2} sx={{ mb: 4 }}>
          {status.devices.map((device) => (
            <DSGrid item xs={12} sm={6} key={device.id}>
              <DeviceCard device={device} />
            </DSGrid>
          ))}
        </DSGrid>
      ) : (
        <DSBox sx={{ mb: 4 }}>
          <DSTypography variant="body2" color="text.secondary">
            No devices connected
          </DSTypography>
        </DSBox>
      )}

      {/* Run test section */}
      <DSTypography variant="h6" sx={{ mb: 2 }}>
        Run Test
      </DSTypography>

      <TestForm
        devices={status?.devices ?? []}
        onTestStarted={handleTestStarted}
        disabled={isTestRunning}
      />

      {/* Real-time test progress */}
      <TestProgress
        deviceStatuses={deviceStatuses}
        isTestRunning={isTestRunning}
        storyProgress={storyProgress}
      />

      {/* Error summary when test completes */}
      {testResult && <ErrorSummaryPanel result={testResult} />}

      {/* Analysis history */}
      <DSTypography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Analysis History
      </DSTypography>

      <AnalysisHistory />
    </DSBox>
  );
};

export default VersionTester;
